// Admin navigation configuration - matching admin-web structure exactly
import { SVG_COMPONENTS } from '@/assets/admin';
import {
  INavigationItem,
  IUserState,
  SELECTOR_OPTIONS,
} from '@/types/admin/navigation';

// Navigation items configuration (matching admin-web useMainNavItemsConfig exactly)
export const ADMIN_NAVIGATION_ITEMS: INavigationItem[] = [
  {
    id: 'community',
    path: '/residents',
    title: 'Community',
    subNavItems: [
      {
        path: '/residents',
        title: 'Residents',
        displaySelectors: [
          SELECTOR_OPTIONS.property,
          SELECTOR_OPTIONS.organization,
        ],
      },
      {
        path: '/events',
        title: 'Events',
        displaySelectors: [
          SELECTOR_OPTIONS.property,
          SELECTOR_OPTIONS.organization,
        ],
      },
      // {
      //   path: '/bulletins',
      //   title: 'Bulletins',
      //   displaySelectors: [SELECTOR_OPTIONS.property, SELECTOR_OPTIONS.organization],
      // },
      {
        path: '/messages',
        title: 'Messages',
        displaySelectors: [
          SELECTOR_OPTIONS.property,
          SELECTOR_OPTIONS.organization,
        ],
      },
      {
        path: '/resources',
        title: 'Resources',
        displaySelectors: [
          SELECTOR_OPTIONS.property,
          SELECTOR_OPTIONS.organization,
        ],
      },
      {
        path: '/todo-lists',
        title: "To Do's",
        displaySelectors: [
          SELECTOR_OPTIONS.property,
          SELECTOR_OPTIONS.organization,
        ],
      },
    ],
  },
  {
    id: 'cork-board',
    path: '/tag-management',
    title: 'Newsfeed',
    subNavItems: [
      {
        path: '/tag-management',
        title: 'Tags',
        displaySelectors: [
          SELECTOR_OPTIONS.property,
          SELECTOR_OPTIONS.organization,
        ],
      },
      {
        path: '/thread-moderation',
        title: 'Threads',
        displaySelectors: [
          SELECTOR_OPTIONS.property,
          SELECTOR_OPTIONS.organization,
        ],
      },
      {
        path: '/report-review',
        title: 'Reports',
        displaySelectors: [
          SELECTOR_OPTIONS.property,
          SELECTOR_OPTIONS.organization,
        ],
      },
      {
        path: '/surveys-management',
        title: 'Surveys',
        displaySelectors: [
          SELECTOR_OPTIONS.property,
          SELECTOR_OPTIONS.organization,
        ],
      },
    ],
  },
  {
    id: 'accounting',
    path: '/funding',
    title: 'Accounting',
    subNavItems: [
      {
        path: '/funding',
        title: 'Funding',
        displaySelectors: [
          SELECTOR_OPTIONS.property,
          SELECTOR_OPTIONS.organization,
        ],
      },
    ],
    condition: (user: IUserState) =>
      user.isSuperUser ||
      user?.roles?.some(role =>
        ['Billing Admin', 'Organization Admin', 'Resio Admin'].includes(
          role.name,
        ),
      ) === true,
  },
  {
    id: 'organization',
    path: '/organizations',
    title: 'Organization',
    subNavItems: [
      {
        path: '/organizations',
        title: 'Organizations',
        condition: (user: IUserState) =>
          user.isSuperUser ||
          user?.roles?.some(role =>
            ['Resio Admin', 'Organization Admin'].includes(role.name),
          ) === true,
      },
      {
        path: '/user-management',
        title: 'Admin User Management',
      },
      {
        path: '/properties',
        title: 'Properties',
        condition: (user: IUserState) =>
          user.isSuperUser ||
          user?.roles?.some(role =>
            ['Resio Admin', 'Organization Admin'].includes(role.name),
          ) === true,
      },
    ],
  },
  {
    id: 'resio-admin',
    path: '/feature-flags',
    title: 'Resio Admin',
    subNavItems: [
      {
        path: '/feature-flags',
        title: 'Feature Flags',
      },
    ],
    condition: (user: IUserState) =>
      user.isSuperUser ||
      user?.roles?.some(role => ['Resio Admin'].includes(role.name)) === true,
  },
];

// Default expanded menu (matching web behavior)
export const DEFAULT_EXPANDED_MENU = 'community';

// Default selected nav item (matching web behavior)
export const DEFAULT_SELECTED_NAV_ITEM = 'residents';

export const FUNDING_PATH = '/funding';

// Group labels keyed by parent navigation item id — used by the shared mobile menu
// to render section headings in the order ADMIN_NAVIGATION_ITEMS defines.
export const ADMIN_NAV_GROUP_LABELS: Record<string, string> =
  ADMIN_NAVIGATION_ITEMS.reduce((acc, item) => {
    acc[item.id] = item.title;
    return acc;
  }, {} as Record<string, string>);

export const ADMIN_NAV_GROUP_ORDER: string[] = ADMIN_NAVIGATION_ITEMS.map(
  item => item.id,
);

// Parent SVG keys for each top-level admin nav group, shared between the
// desktop sidebar (AdminNavList) and the mobile hamburger menu (AdminHeaderWithMenu)
// so the two surfaces never drift.
export const ADMIN_NAV_PARENT_ICON_KEYS: Record<
  string,
  keyof typeof SVG_COMPONENTS
> = {
  community: 'GROUP',
  'cork-board': 'MESSAGE_BLAST',
  accounting: 'NEW_USER',
  organization: 'ORGANIZATION',
  'resio-admin': 'ADMIN',
};

// Per-route SVG keys for admin sub-nav items, mirroring the empty-state icons
// each screen already shows (see AdminEmptyState's EMPTY_STATE_CONFIG).
export const ADMIN_NAV_SUB_ICON_KEYS: Record<
  string,
  keyof typeof SVG_COMPONENTS
> = {
  '/residents': 'NEW_USER',
  '/events': 'CALENDAR_EVENT',
  '/messages': 'EMPTY_MESSAGE',
  '/resources': 'FOLDER',
  '/todo-lists': 'CHECK_CIRCLE',
  '/tag-management': 'EMPTY_MESSAGE',
  '/thread-moderation': 'START_CONVO',
  '/report-review': 'WARNING',
  '/surveys-management': 'EMPTY_MESSAGE',
  '/funding': 'NEW_PROPERTY',
  '/organizations': 'ORGANIZATION',
  '/user-management': 'NEW_USER',
  '/properties': 'NEW_PROPERTY',
  '/feature-flags': 'NEW_PROPERTY',
};
